// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// pattern: Functional Core

// The editor's save status, shown next to the Save button. It is driven ONLY by
// the outcome of the host's onSave call: 'saved' is set when onSave resolves
// with a server version, never optimistically. Anything else -- a thrown error,
// or onSave resolving without a version -- is 'error' with a plain-language
// reason, because a save we can't confirm must not look like a save.
// 'unsaved' means the model changed after the last confirmed save without a
// save being sent (pan/zoom update the stored view but don't auto-save), so
// "Saved" would no longer be true.
export type SaveStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  | { readonly kind: 'unsaved' }
  | { readonly kind: 'error'; readonly reason: string };

export const SaveIdle: SaveStatus = { kind: 'idle' };
export const SaveSaving: SaveStatus = { kind: 'saving' };
export const SaveSaved: SaveStatus = { kind: 'saved' };
export const SaveUnsaved: SaveStatus = { kind: 'unsaved' };

// Plain reasons shown to the user. No status codes or server text on screen --
// the technical detail goes to the console via the host's logging instead.
export const ReasonSignedOut = 'You are signed out. Sign in again, then retry.';
export const ReasonNoPermission = "You don't have permission to save changes to this model.";
export const ReasonUpgrade = "Your plan doesn't include saving models.";
export const ReasonConflict = 'This model was changed somewhere else. Reload the page to get the latest version.';
export const ReasonNotFound = "This model couldn't be found on the server.";
export const ReasonServer = 'The server had a problem saving your changes.';
export const ReasonNetwork = "Couldn't reach the server. Check your connection.";
export const ReasonUnknown = 'Something went wrong while saving.';

/**
 * A save the host attempted and could not complete. `reason` is the
 * user-facing sentence; `detail` is the technical cause for logs only.
 * Hosts throw this from onSave so the Editor can show the reason verbatim.
 */
export class SaveError extends Error {
  name = 'SaveError';
  readonly reason: string;
  readonly detail: string;
  constructor(reason: string, detail: string = reason) {
    super(reason);
    this.reason = reason;
    this.detail = detail;
  }
}

/**
 * Plain reason for a failed save HTTP response. `body` is the parsed JSON
 * error body if there was one; the tier gate's 'upgrade_required' token is
 * recognised so a plan limit isn't reported as a permissions problem.
 */
export function reasonForHttpStatus(status: number, body?: unknown): string {
  if (status === 401) {
    return ReasonSignedOut;
  }
  if (status === 403) {
    const token = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;
    return token === 'upgrade_required' ? ReasonUpgrade : ReasonNoPermission;
  }
  if (status === 404) {
    return ReasonNotFound;
  }
  if (status === 409) {
    return ReasonConflict;
  }
  if (status >= 500) {
    return ReasonServer;
  }
  return ReasonUnknown;
}

/**
 * Plain reason for anything onSave threw. A SaveError carries its own reason;
 * anything else gets the generic sentence. We deliberately don't guess
 * "network" from a bare TypeError: a coding bug throws TypeError too, and
 * telling the user to check their connection would send them the wrong way.
 * Hosts that know a request never reached the server throw
 * SaveError(ReasonNetwork) themselves.
 */
export function reasonForError(err: unknown): string {
  if (err instanceof SaveError) {
    return err.reason;
  }
  return ReasonUnknown;
}
